import React, { useEffect, useState } from 'react';
import { Table, Button, Card, Tag, Typography, Space, Spin, message, Modal, Row, Col, Statistic } from 'antd';
import { ReloadOutlined, CloseOutlined, MailOutlined } from '@ant-design/icons';
import Web3 from 'web3';
import { NFTABI } from './abis.js';
import { getNFTLicenses, revokeNFTLicense, getNFTLicenseAnalytics, sendRenewalReminder } from 'helpers/nftLicense.api';

const { Title, Text } = Typography;

const IssuedLicensesTab = ({ codebase, chainId, daoInfo }) => {
  const [loading, setLoading] = useState(false);
  const [nftLicenses, setNftLicenses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [contractInfo, setContractInfo] = useState({
    nftContractAddress: '',
    nftMarketPlaceAddress: '',
  });

  useEffect(() => {
    if (codebase) {
      setContractInfo({
        nftContractAddress: codebase.nftContractAddress || '',
        nftMarketPlaceAddress: codebase.nftMarketPlaceAddress || '',
      });

      // Load NFT licenses immediately if codebase is available
      if (codebase._id) {
        loadNFTLicenses();
        loadAnalytics();
      }
    }
  }, [codebase]);

  const loadNFTLicenses = async () => {
    if (!codebase?._id) {
      console.warn('No codebase ID available');
      return;
    }

    setLoading(true);
    try {
      const response = await getNFTLicenses(codebase._id);

      // Response structure from client.api: { data: { data: {...} }, status: 200 }
      const responseData = response?.data || response;
      const licensesData = responseData?.data?.licenses || responseData?.licenses || [];

      const licenses = licensesData.map(license => ({
        id: license._id,
        tokenId: license.tokenId,
        status: license.status,
        owner: license.buyerAddress || 'DAO',
        buyerAddress: license.buyerAddress,
        buyerEmail: license.buyerEmail,
        startDate: license.startDate ? new Date(license.startDate) : null,
        endDate: license.endDate ? new Date(license.endDate) : null,
        remainingTime: license.endDate ? Math.max(0, Math.floor((new Date(license.endDate) - new Date()) / 1000)) : 0,
        licenseTerm: license.licenseTerm,
        price: license.price,
        currency: license.currency,
        mintedAt: license.mintedAt ? new Date(license.mintedAt) : null,
        mintedBy: license.mintedBy,
        contractAddress: license.contractAddress,
        marketplaceAddress: license.marketplaceAddress,
        notes: license.notes
      }));

      setNftLicenses(licenses);
      console.log('Loaded NFT licenses from API:', licenses);
    } catch (error) {
      console.error('Error loading NFT licenses:', error);
      message.error('Failed to load NFT licenses from database. ' + (error.response?.data?.message || error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    if (!codebase?._id) return;

    try {
      const response = await getNFTLicenseAnalytics(codebase._id);
      // Response structure from client.api: { data: { data: {...} }, status: 200 }
      const responseData = response?.data || response;
      const analyticsData = responseData?.data || responseData;
      
      if (analyticsData) {
        setAnalytics(analyticsData);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const checkMetaMaskLogin = async () => {
    if (!window.ethereum) {
      return false;
    }

    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      return true;
    } catch (error) {
      return false;
    }
  };

  const revokeNFTOnChain = async (license, daoInfo) => {
    const isMetaMaskLoggedIn = await checkMetaMaskLogin();
    if (!isMetaMaskLoggedIn) {
      throw new Error('Please log in to MetaMask to proceed.');
    }

    const web3 = new Web3(window.ethereum);
    const currentChainId = await web3.eth.getChainId();
    const desiredChainId = daoInfo?.chainId || chainId;

    // Switch network if needed
    if (currentChainId.toString() !== desiredChainId.toString()) {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: web3.utils.toHex(desiredChainId) }],
        });
      } catch (switchError) {
        if (switchError.code === 4902) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: web3.utils.toHex(desiredChainId),
              }],
            });
          } catch (addError) {
            throw new Error('Unable to add the chain to MetaMask.');
          }
        } else {
          throw new Error('Unable to switch to the correct network.');
        }
      }
    }

    const contractNFT = new web3.eth.Contract(NFTABI, license.contractAddress);
    try {
      const accounts = await web3.eth.getAccounts();

      // Get gas price and increase by 10%
      const gasPrice = await web3.eth.getGasPrice();
      const aggressiveGasPrice = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(11)).div(web3.utils.toBN(10));

      const result = await contractNFT.methods.revoke(license.buyerAddress).send({
        from: accounts[0],
        gasPrice: aggressiveGasPrice.toString(),
      });

      return {
        success: true,
        transactionHash: result.transactionHash,
        receipt: result
      };
    } catch (error) {
      console.error('Error while revoking NFT on blockchain:', error);
      throw new Error('Failed to revoke NFT on blockchain: ' + error.message);
    }
  };

  const handleRevokeLicense = async (licenseId, tokenId, license) => {
    Modal.confirm({
      title: 'Revoke License',
      content: `Are you sure you want to revoke license for Token #${tokenId}?`,
      okText: 'Yes, Revoke',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);

          const daoInfoData = daoInfo || { chainId: chainId };

          if (!license.buyerAddress && license.status !== 'minted') {
            throw new Error('Cannot revoke: Buyer address not found for this license.');
          }

          if (license.status === 'active' && license.buyerAddress) {
            console.log('Revoking NFT on blockchain...');
            const blockchainResult = await revokeNFTOnChain(license, daoInfoData);

            if (blockchainResult.success) {
              console.log('NFT revoked on blockchain successfully:', blockchainResult.transactionHash);
              
              console.log('Updating license status in database...');
              const response = await revokeNFTLicense(licenseId);
              
              // Response structure from client.api: { data: {...}, status: 200 }
              if (response?.data || response) {
                message.success('License revoked successfully!');
                loadNFTLicenses();
                loadAnalytics();
              } else {
                throw new Error(response?.data?.message || response?.message || 'Failed to update license status in database');
              }
            } else {
              throw new Error('Failed to revoke NFT on blockchain');
            }
          } else {
            // For non-active licenses, just update in database
            const response = await revokeNFTLicense(licenseId);
            // Response structure from client.api: { data: {...}, status: 200 }
            if (response?.data || response) {
              message.success('License revoked successfully!');
              loadNFTLicenses();
              loadAnalytics();
            } else {
              throw new Error(response?.data?.message || response?.message || 'Failed to update license status in database');
            }
          }
        } catch (error) {
          console.error('Error revoking license:', error);
          message.error('Failed to revoke license: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSendReminder = async (licenseId, tokenId, buyerEmail) => {
    if (!buyerEmail) {
      message.warning('Cannot send reminder: No buyer email found for this license.');
      return;
    }

    Modal.confirm({
      title: 'Send Renewal Reminder',
      content: `Send renewal reminder email to ${buyerEmail} for Token #${tokenId}?`,
      okText: 'Yes, Send',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await sendRenewalReminder(licenseId);
          // Response structure from client.api: { data: {...}, status: 200 }
          if (response?.data || response) {
            message.success('Renewal reminder email sent successfully!');
            loadNFTLicenses(); // Refresh the list to update reminder history
          } else {
            throw new Error(response?.data?.message || response?.message || 'Failed to send reminder');
          }
        } catch (error) {
          console.error('Error sending reminder:', error);
          message.error('Failed to send reminder: ' + (error.response?.data?.message || error.message || error));
        }
      }
    });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const formatRemainingTime = (seconds) => {
    if (seconds <= 0) return 'Expired';
    
    const days = Math.floor(seconds / (24 * 3600));
    const hours = Math.floor((seconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days} days, ${hours} hours`;
    } else if (hours > 0) {
      return `${hours} hours, ${minutes} minutes`;
    } else {
      return `${minutes} minutes`;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'available':
        return 'default';
      case 'expired':
        return 'error';
      case 'minted':
        return 'processing';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (license) => {
    if (license.status === 'active' && license.remainingTime > 0) {
      return `Active (${formatRemainingTime(license.remainingTime)})`;
    }
    return license.status || 'Unknown';
  };

  const columns = [
    {
      title: 'Token ID',
      dataIndex: 'tokenId',
      key: 'tokenId',
      render: (tokenId) => `#${tokenId}`,
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_, record) => (
        <Tag color={getStatusColor(record.status)}>
          {getStatusLabel(record)}
        </Tag>
      ),
      width: 150,
    },
    {
      title: 'Buyer Email',
      dataIndex: 'buyerEmail',
      key: 'buyerEmail',
      render: (email) => email || 'N/A',
      width: 200,
    },
    {
      title: 'License Term',
      dataIndex: 'licenseTerm',
      key: 'licenseTerm',
      render: (term) => term ? `${term} years` : 'N/A',
      width: 120,
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date) => formatDate(date),
      width: 180,
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date) => formatDate(date),
      width: 180,
    },
    {
      title: 'Remaining Time',
      dataIndex: 'remainingTime',
      key: 'remainingTime',
      render: (remainingTime, record) => 
        record.status === 'active' ? formatRemainingTime(remainingTime) : 'N/A',
      width: 150,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {record.status === 'active' && (
            <Button
              size="small"
              icon={<MailOutlined />}
              onClick={() => handleSendReminder(record.id, record.tokenId, record.buyerEmail)}
              disabled={!record.buyerEmail}
              title={!record.buyerEmail ? 'No buyer email available' : 'Send renewal reminder email'}
            >
              Send Reminder
            </Button>
          )}
          {record.status === 'active' && record.buyerAddress && (
            <Button
              size="small"
              danger
              icon={<CloseOutlined />}
              onClick={() => handleRevokeLicense(record.id, record.tokenId, record)}
            >
              Revoke
            </Button>
          )}
        </Space>
      ),
      width: 200,
    },
  ];

  return (
    <div style={{ padding: '24px 0' }}>
      <Card style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 16 }}>NFT License Management</Title>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">Contract Address: </Text>
            <Text code>{contractInfo.nftContractAddress || 'Not set'}</Text>
          </div>
          <div>
            <Text type="secondary">Marketplace Address: </Text>
            <Text code>{contractInfo.nftMarketPlaceAddress || 'Not set'}</Text>
          </div>
          
          {analytics && (
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={6}>
                <Statistic title="Total" value={analytics.total || 0} />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Active" 
                  value={analytics.active || 0} 
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Expired" 
                  value={analytics.expired || 0} 
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Expiring Soon" 
                  value={analytics.expiringSoon || 0} 
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
            </Row>
          )}
          
          <div>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                loadNFTLicenses();
                loadAnalytics();
              }}
              loading={loading}
            >
              Refresh Issued Licenses
            </Button>
          </div>
        </Space>
      </Card>

      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={nftLicenses}
            rowKey={(record) => `${record.contractAddress}-${record.tokenId}`}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} licenses`,
            }}
            scroll={{ x: 1200 }}
            locale={{
              emptyText: loading 
                ? 'Loading...' 
                : 'No NFT licenses found. Click "Refresh Issued Licenses" to load data.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default IssuedLicensesTab;

